import { createSystem, defaultConfig, mergeConfigs } from '@chakra-ui/react'
import { typography } from '@reward-platform/lift/theme/aer_lingus/typography'
import { alBoxShadow } from '@reward-platform/lift/theme/aer_lingus/variables'
import { button } from '@reward-platform/lift/components/ChakraButton/themes/aerLingus'
import { snackbar } from '@reward-platform/lift/components/Snackbar/themes/aerLingus'
import { customTabs } from '@reward-platform/lift/components/Tabs/themes/aerLingus'
import { stickyBanner } from '@reward-platform/lift/components/StickyBanner/themes/aerLingus'
import { label } from '~/components/search-and-results/Label/themes/aerLingus'
import { footer } from '~/components/shared/Footer/themes/aerLingus'
import { imageCard } from '~/components/search-and-results/Card/themes/aerLingus'
import { continueButton } from '~/components/search-and-results/ContinueButton/themes/aerLingus'
import { showHideButton } from '~/components/search-and-results/ShowHideButton/themes/aerLingus'
import { baseTheme } from './base'

const primaryTeal = 'rgb({colors.rgb.alPrimaryTeal})'
const outlineColor = primaryTeal

const focusOutline = {
  outlineColor,
  outlineWidth: 1,
  outlineStyle: 'solid',
}

export const aerLingusTheme = createSystem(
  defaultConfig,
  mergeConfigs(baseTheme, {
    globalCss: {
      'html, body, div#__next': {
        color: 'brand.black',
      },
      body: {
        '& input': {
          _placeholder: {
            color: 'brand.coolgrey10',
          },
        },
      },
      '*:focus-visible': {
        ...focusOutline,
      },
      a: {
        color: 'brand.primaryTeal',
      },
    },

    theme: {
      tokens: {
        shadows: {
          brand: {
            value: alBoxShadow,
          },
          floating: {
            value: '0rem 0.5rem 2rem 0rem rgba(0, 0, 0, 0.29)',
          },
          light: {
            value: '0rem 0.125rem 0.25rem 0rem rgba(0, 0, 0, 0.08)',
          },
        },

        fonts: {
          heading: {
            value: typography.families.heading,
          },
          body: {
            value: typography.families.body,
          },
        },

        fontSizes: {
          '2xl-5': {
            value: '1.375rem',
          },
          '4xl-5': {
            value: '2.5rem',
          },
        },

        spacing: {
          '4-5': {
            value: '1.125rem',
          },
        },

        colors: {
          rgb: {
            alPrimaryTeal: {
              value: '0, 98, 114',
            },
            alTealDarker: {
              value: '0, 78, 91',
            },
            alTeal10: {
              value: '229, 239, 240',
            },
            alCloudWhite: {
              value: '249, 249, 249',
            },
            alBlack: {
              value: '44, 42, 41',
            },
            alRed: {
              value: '197, 80, 80',
            },
            alWhite: {
              value: '255, 255, 255',
            },
            alCoastalJetty: {
              value: '0, 110, 128',
            },
            alCoolGrey4: {
              value: '187, 188, 188',
            },
            alCoolGrey10: {
              value: '99, 102, 106',
            },
            alCoolGrey2: {
              value: '208, 208, 206',
            },
            alBackgroundGrey: {
              value: '247, 247, 247',
            },
            alTeal: {
              value: '0, 120, 140',
            },
            alPrimaryShamrockGreen: {
              value: '132, 189, 0',
            },
            alCityGreen: {
              value: '18, 173, 158',
            },
            alCityGreen5: {
              value: '231, 247, 245',
            },
            alDarkCityGreen: {
              value: '0, 133, 120',
            },
            alSkyBlue: {
              value: '0, 149, 203',
            },
            alExtraLegroom: {
              value: '77, 208, 225',
            },
            alSeaBlue: {
              value: '0, 103, 185',
            },
            alOrange: {
              value: '231, 114, 0',
            },
            alCoolGrey1: {
              value: '241, 241, 241',
            },
            alCoolGrey8: {
              value: '136, 139, 141',
            },
            alTeal5: {
              value: '242, 247, 248',
            },
            alOrange5: {
              value: '254, 248, 242',
            },
            alOrange30: {
              value: '248, 209, 178',
            },
            alRed5: {
              value: '252, 246, 246',
            },
            alShamrock5: {
              value: '249, 251, 242',
            },
            alSkyBlue5: {
              value: '242, 249, 252',
            },
            alBGTeal10: {
              value: '229, 239, 240',
            },
            alYellow: {
              value: '242, 204, 6',
            },
            alTeal15: {
              value: '0, 76, 89',
            },
            alHighlightOrange: {
              value: '231, 114, 0',
            },
            alSales: {
              value: '218, 61, 0',
            },
          },
          brand: {
            white: {
              value: 'rgb({colors.rgb.alWhite})',
            },
            primary: {
              value: primaryTeal,
            },
            primaryTealAlpha20: {
              value: 'rgba({colors.rgb.alPrimaryTeal}, 0.2)',
            },
            primaryShamrockGreen: {
              value: 'rgb({colors.rgb.alPrimaryShamrockGreen})',
            },
            shamrockGreen10: {
              value: 'rgba({colors.rgb.alPrimaryShamrockGreen}, 0.1)',
            },
            coolgrey1: {
              value: 'rgb({colors.rgb.alCoolGrey1})',
            },
            coolgrey2: {
              value: 'rgb({colors.rgb.alCoolGrey2})',
            },
            coolgrey4: {
              value: 'rgb({colors.rgb.alCoolGrey4})',
            },
            coolgrey8: {
              value: 'rgb({colors.rgb.alCoolGrey8})',
            },
            coolgrey10: {
              value: 'rgb({colors.rgb.alCoolGrey10})',
            },
            backgroundGrey: {
              value: 'rgb({colors.rgb.alBackgroundGrey})',
            },
            coastalJetty: {
              value: 'rgb({colors.rgb.alCoastalJetty})',
            },
            black: {
              value: 'rgb({colors.rgb.alBlack})',
            },
            teal: {
              value: 'rgb({colors.rgb.alTeal})',
            },
            tealDarker: {
              value: 'rgb({colors.rgb.alTealDarker})',
            },
            teal5: {
              value: 'rgb({colors.rgb.alTeal5})',
            },
            teal15: {
              value: 'rgb({colors.rgb.alTeal15})',
            },
            red: {
              value: 'rgb({colors.rgb.alRed})',
            },
            red5: {
              value: 'rgb({colors.rgb.alRed5})',
            },
            teal10: {
              value: 'rgb({colors.rgb.alTeal10})',
            },
            cloudWhite: {
              value: 'rgb({colors.rgb.alCloudWhite})',
            },
            cityGreen: {
              value: 'rgb({colors.rgb.alCityGreen})',
            },
            cityGreen5: {
              value: 'rgb({colors.rgb.alCityGreen5})',
            },
            darkCityGreen: {
              value: 'rgb({colors.rgb.alDarkCityGreen})',
            },
            shamrock5: {
              value: 'rgb({colors.rgb.alShamrock5})',
            },
            orange: {
              value: 'rgb({colors.rgb.alOrange})',
            },
            orange5: {
              value: 'rgb({colors.rgb.alOrange5})',
            },
            orange30: {
              value: 'rgb({colors.rgb.alOrange30})',
            },
            bgteal10: {
              value: 'rgb({colors.rgb.alBGTeal10})',
            },
            icon: {
              value: 'rgb({colors.rgb.alBlack})',
            },
            inputIcon: {
              value: 'rgb({colors.rgb.alPrimaryTeal})',
            },
            disabledIcon: {
              value: 'rgb({colors.rgb.alCoolGrey4})',
            },
            highlightOrange: {
              value: 'rgb({colors.rgb.alHighlightOrange})',
            },
            focusOutline: {
              value: outlineColor,
            },
            sales: {
              value: 'rgb({colors.rgb.alSales})',
            },
          },
          error: {
            primary: {
              value: 'rgb({colors.rgb.alRed})',
            },
          },
        },
      },

      recipes: {
        // Shared
        button,
      },

      slotRecipes: {
        // Deprecated
        customTabs,

        // Shared
        snackbar,
        stickyBanner,
        footer,

        // Flights
        label,
        continueButton,
        showHideButton,

        // Search & Results
        imageCard,
      },
    },
  })
)
